import { useContext, useEffect, useState } from 'react'
import { UIContext } from 'Context/UI'
import { useNavigate } from 'react-router-dom'
import { useFileDownloadStore } from 'Store/FileDownload'
import { useProfileStore } from 'Store/Profile'
import { useCallbackPrompt } from 'Hooks/useCallbackPrompt'
import { TABS } from '../Config'

import { toast } from 'react-toastify'
import { shallow } from 'zustand/shallow'
import { extractFileName } from 'Utils/extractFileName'
import { generateDownloadUrl } from 'Utils/generateDownloadUrl'
import Swal from 'sweetalert2'
import * as Yup from 'yup'

const VIEW_INDEX = 3

const ViewModel = () => {
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const [currentTab, setCurrentTab] = useState(VIEW_INDEX)
	const navigate = useNavigate()
	const { profile, getProfile, updateProfile } = useProfileStore(
		state => ({
			profile: state.profile,
			getProfile: state.getProfile,
			updateProfile: state.updateProfile,
		}),
		shallow
	)

	const validationSchema = Yup.object().shape({
		bankAccountHolder: Yup.string().required('กรุณากรอกชื่อบัญชีธนาคาร'),
		bankAccountNumber: Yup.string().test('len', 'กรุณากรอกเลขที่บัญชีธนาคารให้ครบถ้วน', val => {
			if (val) {
				return (
					val.replace(/-/g, '').length === 10 ||
					val.replace(/-/g, '').length === 12 ||
					val.replace(/-/g, '').length === 15
				)
			}
			return false
		}),
		bankAccountName: Yup.string().required('กรุณาเลือกธนาคาร'),
		bankAccountPicture: Yup.mixed().required('กรุณาอัพโหลดสำเนาสมุดบัญชีธนาคาร'),
	})

	const [formState, setFormState] = useState({
		bankAccountHolder: '',
		bankAccountNumber: '',
		bankAccountName: '',
		bankAccountPicture: '',
	})
	const [hasChanged, setHasChanged] = useState(false)
	const [hasUploaded, setHasUploaded] = useState(false)
	const [error, setError] = useState<{ [key: string]: string }>({})
	const [isSubmitting, setIsSubmitting] = useState(false)

	const downloadFile = useFileDownloadStore(state => state.download)

	useCallbackPrompt(hasChanged)

	const findTabPath = () => {
		const tab = TABS.find((_, idx) => idx === currentTab)
		if (tab) {
			return tab.path
		}
	}

	const onChange = (name: string, value: any) => {
		setFormState({
			...formState,
			[name]: value,
		})
		setHasChanged(true)

		if (name === 'bankAccountPicture') {
			setHasUploaded(true)
		}

		validationSchema
			.validate(
				{
					...formState,
					[name]: value,
				},
				{ abortEarly: false }
			)
			.then(function () {
				setError({})
			})
			.catch(function (err) {
				setError(
					err.inner.reduce((all: any[], e: any) => {
						all[e.path] = e.message

						return all
					}, {})
				)
			})
	}
	const onDownload = (key: string) => {
		// @ts-ignore
		const filePath = profile[key]
		if (!filePath) return
		else {
			downloadFile(
				generateDownloadUrl(filePath),
				extractFileName(generateDownloadUrl(filePath))
			).then(({ success, data }) => {
				if (!success) {
					Swal.fire({
						icon: 'error',
						title: 'ดาวน์โหลดไฟล์ไม่สำเร็จ',
						text: data,
						timer: 1500,
						showConfirmButton: false,
						showCancelButton: false,
					})
				}
			})
		}
	}

	const onSubmit = async () => {
		if (hasChanged) {
			setIsSubmitting(true)
			const requiredFields = [
				'bankAccountHolder',
				'bankAccountNumber',
				'bankAccountName',
				'bankAccountPicture',
			]

			const allFields = [...requiredFields]

			validationSchema
				.validate(formState, { abortEarly: false })
				.then(function () {
					setError({})
					const body: { [key: string]: any } = {}

					allFields.forEach(key => {
						if (key === 'bankAccountPicture') {
							// If file is exist
							if (profile[key] !== null) {
								// If file is File
								// @ts-ignore
								if (formState[key] && typeof formState[key] !== 'string') {
									// @ts-ignore
									body[key] = formState[key]
								}
								// If file is deleted
								// @ts-ignore
								else if (!formState[key]) {
									// @ts-ignore
									body[key] = null
								}
							}
							// If file is empty
							else {
								// @ts-ignore
								if (formState[key]) {
									// @ts-ignore
									body[key] = formState[key]
								}
							}
						} else {
							// @ts-ignore
							if (formState[key] !== profile[key]) {
								if (key === 'bankAccountNumber') {
									// @ts-ignore
									body[key] = formState[key].replace(/-/g, '')
								} else {
									// @ts-ignore
									body[key] = formState[key]
								}
							}
						}
					})

					updateProfile(body).then(({ success, data }) => {
						if (!success) {
							toast.error(data)
							setIsSubmitting(false)
							return
						} else {
							setHasChanged(false)
							getProfile().then(({ data: pData }) => {
								setFormState(pData)
								toast.success('บันทึกข้อมูลสำเร็จ')
								setIsSubmitting(false)
							})
						}
					})
				})
				.catch(function (err) {
					toast.error('กรุณากรอกข้อมูลให้ครบถ้วน')
					setError(
						err.inner.reduce((all: any[], e: any) => {
							all[e.path] = e.message

							return all
						}, {})
					)
					setIsSubmitting(false)
				})
		}
	}

	useEffect(() => {
		if (profile) {
			setFormState(profile)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (currentTab !== VIEW_INDEX) {
			navigate(findTabPath() ?? '#')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentTab])

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		currentTab,
		companyName: profile?.companyName,
		setCurrentTab,
		error,
		isSubmitting,
		hasUploaded,
		formState,
		onChange,
		onDownload,
		onSubmit,
	}
}

export default ViewModel
